// Memes Module - Redesigned with AI agent
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Smile, Plus, Share2, Download, Trash2, Image as ImageIcon,
  RefreshCw, Twitter, Facebook, Copy, X, Sparkles, Search
} from 'lucide-react';
import AIAgentModule from './AIAgentModule';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MEME_TEMPLATES = [
  { id: 'drake', name: 'Drake', format: 'top_bottom', image: 'https://api.memegen.link/images/drake.jpg' },
  { id: 'doge', name: 'Doge', format: 'top_bottom', image: 'https://api.memegen.link/images/doge.jpg' },
  { id: 'db', name: 'Distracted Boyfriend', format: 'three_labels', image: 'https://api.memegen.link/images/db.jpg' },
  { id: 'gru', name: 'Plan de Gru', format: 'four_panels', image: 'https://api.memegen.link/images/gru.jpg' },
  { id: 'cmm', name: 'Change My Mind', format: 'single_label', image: 'https://api.memegen.link/images/cmm.jpg' },
  { id: 'fine', name: 'This Is Fine', format: 'two_labels', image: 'https://api.memegen.link/images/fine.jpg' },
  { id: 'disastergirl', name: 'Disaster Girl', format: 'top_bottom', image: 'https://api.memegen.link/images/disastergirl.jpg' },
  { id: 'aag', name: 'Ancient Aliens', format: 'top_bottom', image: 'https://api.memegen.link/images/aag.jpg' },
  { id: 'fry', name: 'Futurama Fry', format: 'top_bottom', image: 'https://api.memegen.link/images/fry.jpg' },
  { id: 'success', name: 'Success Kid', format: 'top_bottom', image: 'https://api.memegen.link/images/success.jpg' },
  { id: 'blb', name: 'Bad Luck Brian', format: 'top_bottom', image: 'https://api.memegen.link/images/blb.jpg' },
  { id: 'buzz', name: 'X Everywhere', format: 'top_bottom', image: 'https://api.memegen.link/images/buzz.jpg' },
];

const getFieldsCount = (format) => {
  const map = { top_bottom: 2, three_labels: 3, four_panels: 4, two_labels: 2, single_label: 1 };
  return map[format] || 2;
};

const getFieldLabels = (format) => {
  const map = {
    top_bottom: ['Texto superior', 'Texto inferior'],
    three_labels: ['Texto 1', 'Texto 2', 'Texto 3'],
    four_panels: ['Panel 1', 'Panel 2', 'Panel 3', 'Panel 4'],
    two_labels: ['Opción 1', 'Opción 2'],
    single_label: ['Tu texto'],
  };
  return map[format] || ['Texto superior', 'Texto inferior'];
};

const MemesModule = ({ user }) => {
  const [templates, setTemplates] = useState(MEME_TEMPLATES);
  const [memes, setMemes] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [texts, setTexts] = useState(['', '', '', '']);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  useEffect(() => {
    loadMemes();
    loadTemplatesFromAPI();
  }, [user.id]);

  const loadTemplatesFromAPI = async () => {
    try {
      const res = await axios.get(`${API}/memes/templates`);
      if (res.data.templates?.length > 0) {
        const apiTemplates = res.data.templates.map(t => ({
          ...t,
          image: `https://api.memegen.link/images/${t.id}.jpg`
        }));
        setTemplates(apiTemplates);
      }
    } catch { setTemplates(MEME_TEMPLATES); }
  };

  const loadMemes = async () => {
    try {
      const res = await axios.get(`${API}/memes/${user.id}`);
      setMemes(res.data || []);
    } catch (e) {}
  };

  const selectTemplate = (template) => {
    setSelectedTemplate(template);
    setTexts(['', '', '', '']);
    setPreviewUrl(template.image);
  };

  const updatePreview = (template, currentTexts) => {
    const clean = currentTexts.filter(t => t).map(t =>
      t.replace(/ /g, '_').replace(/\?/g, '~q').replace(/#/g, '~h').replace(/\//g, '~s').replace(/"/g, "''")
    );
    setPreviewUrl(clean.length > 0 ? `https://api.memegen.link/images/${template.id}/${clean.join('/')}.png` : template.image);
  };

  const handleTextChange = (index, value) => {
    const newTexts = [...texts];
    newTexts[index] = value;
    setTexts(newTexts);
    if (selectedTemplate) updatePreview(selectedTemplate, newTexts);
  };

  const createMeme = async () => {
    if (!selectedTemplate || !texts.some(t => t)) { toast.error('Selecciona una plantilla y agrega texto'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/memes/create?user_id=${user.id}`, { template_id: selectedTemplate.id, texts: texts.filter(t => t) });
      toast.success('Meme guardado');
      setShowCreate(false);
      setSelectedTemplate(null);
      setTexts(['', '', '', '']);
      setPreviewUrl(null);
      loadMemes();
    } catch { toast.error('Error al crear meme'); }
    finally { setLoading(false); }
  };

  const deleteMeme = async (memeId) => {
    try {
      await axios.delete(`${API}/memes/${memeId}`);
      toast.success('Eliminado');
      loadMemes();
    } catch (e) {}
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copiada');
  };

  const downloadMeme = (url, filename) => {
    const a = document.createElement('a'); a.href = url; a.download = filename || 'meme.png'; a.target = '_blank';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const shareToSocial = (platform, meme) => {
    const url = encodeURIComponent(meme.image_url);
    const text = encodeURIComponent('¡Mira este meme!');
    const urls = {
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    };
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const filteredTemplates = templates.filter(t =>
    !searchFilter || t.name.toLowerCase().includes(searchFilter.toLowerCase())
  );

  return (
    <div className="p-4 md:p-6 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Smile className="text-yellow-400" size={24} />Memes
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Crea y comparte memes con tus amigos</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2">
          <Plus size={18} />Crear Meme
        </button>
      </div>

      {/* Create Meme Panel */}
      {showCreate && (
        <div className="glass-panel p-5 mb-6 rounded-2xl animate-fade-in-up">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Sparkles size={18} className="text-yellow-400" />Crear Nuevo Meme
            </h3>
            <button onClick={() => { setShowCreate(false); setSelectedTemplate(null); setPreviewUrl(null); }}
              className="p-1.5 hover:bg-white/10 rounded-lg"><X size={18} className="text-gray-400" /></button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Template selector */}
            <div>
              <div className="relative mb-3">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input value={searchFilter} onChange={e => setSearchFilter(e.target.value)}
                  placeholder="Buscar plantilla..." className="input-glass pl-8 w-full text-sm" />
              </div>
              <div className="grid grid-cols-3 gap-2 max-h-52 overflow-y-auto custom-scrollbar">
                {filteredTemplates.map(t => (
                  <button key={t.id} onClick={() => selectTemplate(t)}
                    className={`relative rounded-xl overflow-hidden border-2 transition-all hover:scale-[1.03] ${selectedTemplate?.id === t.id ? 'border-yellow-400' : 'border-transparent'}`}>
                    <img src={t.image} alt={t.name} className="w-full h-16 object-cover" onError={e => { e.target.src = 'https://via.placeholder.com/100x100?text=Meme'; }} />
                    <div className="absolute inset-0 bg-black/40 flex items-end p-1">
                      <p className="text-white text-xs font-medium leading-tight truncate">{t.name}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Editor + preview */}
            <div>
              {selectedTemplate ? (
                <div className="space-y-3">
                  {/* Preview */}
                  {previewUrl && (
                    <div className="bg-black/30 rounded-xl overflow-hidden flex items-center justify-center" style={{ minHeight: 160 }}>
                      <img src={previewUrl} alt="Preview" className="max-h-48 object-contain rounded-xl"
                        onError={e => { e.target.src = selectedTemplate.image; }} />
                    </div>
                  )}
                  {/* Text fields */}
                  {getFieldLabels(selectedTemplate.format).slice(0, getFieldsCount(selectedTemplate.format)).map((label, i) => (
                    <input key={i} value={texts[i] || ''} onChange={e => handleTextChange(i, e.target.value)}
                      placeholder={label} className="input-glass w-full text-sm" />
                  ))}
                  <div className="flex gap-2 pt-2">
                    <button onClick={() => { setSelectedTemplate(null); setPreviewUrl(null); }} className="btn-secondary flex-1 text-sm">Cambiar</button>
                    <button onClick={createMeme} disabled={loading}
                      className="btn-primary flex-1 text-sm flex items-center justify-center gap-1.5">
                      {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Plus size={16} />Guardar</>}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                  <ImageIcon size={48} className="text-gray-600 mb-3" />
                  <p className="text-gray-500 text-sm">Selecciona una plantilla</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* My Memes Gallery */}
      {memes.length > 0 ? (
        <div>
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <ImageIcon size={18} className="text-gray-400" />Mis Memes ({memes.length})
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {memes.map(meme => (
              <div key={meme.id} className="glass-panel p-0 rounded-2xl overflow-hidden group">
                <div className="relative aspect-square bg-black/30">
                  <img src={meme.image_url} alt="Meme" className="w-full h-full object-cover"
                    onError={e => { e.target.src = 'https://via.placeholder.com/200x200?text=Meme'; }} />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button onClick={() => copyToClipboard(meme.image_url)}
                      className="p-2 bg-white/20 rounded-xl hover:bg-white/30 transition-colors">
                      <Copy size={16} className="text-white" />
                    </button>
                    <button onClick={() => downloadMeme(meme.image_url, `meme-${meme.id}.png`)}
                      className="p-2 bg-white/20 rounded-xl hover:bg-white/30 transition-colors">
                      <Download size={16} className="text-white" />
                    </button>
                    <button onClick={() => shareToSocial('twitter', meme)}
                      className="p-2 bg-blue-500/50 rounded-xl hover:bg-blue-500/70 transition-colors">
                      <Twitter size={16} className="text-white" />
                    </button>
                    <button onClick={() => deleteMeme(meme.id)}
                      className="p-2 bg-red-500/50 rounded-xl hover:bg-red-500/70 transition-colors">
                      <Trash2 size={16} className="text-white" />
                    </button>
                  </div>
                </div>
                <div className="p-2.5">
                  <p className="text-gray-400 text-xs truncate">{meme.template_id}</p>
                  <p className="text-gray-600 text-xs">{new Date(meme.created_at).toLocaleDateString('es-MX')}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : !showCreate ? (
        <div className="glass-panel p-16 rounded-2xl text-center">
          <Smile size={56} className="text-yellow-400/30 mx-auto mb-4" />
          <h3 className="text-white font-semibold mb-2">Sin memes aún</h3>
          <p className="text-gray-500 text-sm mb-5">Crea tu primer meme y compártelo con el mundo</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary">Crear primer meme</button>
        </div>
      ) : null}

      <AIAgentModule sectionContext="memes y humor" compact={true} placeholder="Sugiere ideas para memes..." />
    </div>
  );
};

export default MemesModule;
